import React, { useEffect, useState, useRef } from "react";
import {
  Autocomplete,
  Button,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Stack,
  InputAdornment,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { RemoveCircleOutline, Search } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useAuth } from "../../../../context";
import useAdd from "../../../../api/useAdd";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const getCurrentDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const PurchaseDrug = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const [medicineCompanyData, setMedicineCompanyData] = useState([]);
  const navigate = useNavigate();

  const { data: medicines } = useFetchObjects(["medicine"], "medicines", token);

  const { data: getMedicineCompanyData, isSuccess } = useFetchObjects(
    ["medicineCompanies"],
    `medicine-companies/`,
    token,
  );

  useEffect(() => {
    if (isSuccess) {
      setMedicineCompanyData(
        Array.isArray(getMedicineCompanyData) ? getMedicineCompanyData : [],
      );
    }
  }, [getMedicineCompanyData, isSuccess]);

  const [markupPercentage, setMarkupPercentage] = useState(15);
  const [input, setInput] = useState({
    date: getCurrentDate(),
    medicine_company: null,
    items: [],
  });
  const [formErrors, setFormErrors] = useState({});
  const [autocompleteInputValue, setAutocompleteInputValue] = useState("");

  const {
    handleAdd: handleAddPurchase,
    resetForm,
    loading: purchaseLoading,
  } = useAdd(
    "purchases",
    token,
    t("pharmacy.purchaseSuccess"),
    t("pharmacy.operationFailed"),
  );

  const autocompleteRef = useRef(null);
  const quantityRefs = useRef([]);
  const purchasePriceRefs = useRef([]);
  const sellPriceRefs = useRef([]);
  const expiryDateRefs = useRef([]);

  useEffect(() => {
    if (resetForm) {
      setInput({
        date: getCurrentDate(),
        medicine_company: null,
        items: [],
      });
      setFormErrors({});
    }
  }, [resetForm]);

  const handleChangePurchaseField = (e) => {
    const { name, value } = e.target;
    setInput({ ...input, [name]: value });
  };

  const handleChangeMarkup = (e) => {
    const newMarkupPercentage = parseFloat(e.target.value) || 0;
    setMarkupPercentage(newMarkupPercentage);

    setInput((prevInput) => {
      const updatedItems = prevInput.items.map((item) => {
        const purchasePrice = parseFloat(item.purchase_price) || 0;
        return {
          ...item,
          sell_price: Math.round(
            purchasePrice + (purchasePrice * newMarkupPercentage) / 100,
          ),
        };
      });
      return {
        ...prevInput,
        items: updatedItems,
      };
    });
  };

  const handleAddNewItem = (medicine) => {
    if (medicine) {
      const newItem = {
        medicine: medicine.id,
        name: medicine.name,
        quantity: "",
        purchase_price: "",
        sell_price: "",
        expiry_date: "",
        subtotal: 0,
      };

      setInput((prevInput) => {
        const newItems = [...prevInput.items, newItem];
        return {
          ...prevInput,
          items: newItems,
        };
      });

      setAutocompleteInputValue("");
      if (autocompleteRef.current) {
        autocompleteRef.current.focus();
      }
    }
  };

  const handleChangeItemField = (index, e) => {
    const { name, value } = e.target;
    const items = [...input.items];
    items[index][name] = value;

    if (name === "purchase_price") {
      // Automatically calculate the sell price based on the provided markup percentage
      const purchasePrice = parseFloat(value) || 0;
      items[index].sell_price = Math.round(
        purchasePrice + (purchasePrice * markupPercentage) / 100,
      );
    }

    if (name === "quantity" || name === "purchase_price") {
      const quantity = parseFloat(items[index].quantity) || 0;
      const purchasePrice = parseFloat(items[index].purchase_price) || 0;
      items[index].subtotal = quantity * purchasePrice;
    }

    setInput({ ...input, items });

    // Auto-focus logic for moving between inputs using the Enter key
    if (e.key === "Enter") {
      if (name === "quantity" && purchasePriceRefs.current[index]) {
        purchasePriceRefs.current[index].focus();
      } else if (name === "purchase_price" && sellPriceRefs.current[index]) {
        sellPriceRefs.current[index].focus();
      } else if (name === "sell_price" && expiryDateRefs.current[index]) {
        expiryDateRefs.current[index].focus();
      } else if (name === "expiry_date" && index < input.items.length - 1) {
        // Move to the next item's quantity field
        if (quantityRefs.current[index + 1]) {
          quantityRefs.current[index + 1].focus();
        }
      }
    }
  };

  const handleRemoveItem = (index) => {
    const items = [...input.items];
    items.splice(index, 1);
    setInput({ ...input, items });
  };

  const handleSave = (e) => {
    e.preventDefault();
    let isValid = true;
    let errors = {};

    if (!input.date) {
      errors.date = t("pharmacy.purchaseRequired");
      isValid = false;
    }

    input.items.forEach((item, index) => {
      if (!item.medicine) {
        errors[`medicine_${index}`] = t("pharmacy.medicineRequired");
        isValid = false;
      }
      if (!item.quantity) {
        errors[`quantity_${index}`] = t("pharmacy.quantityRequired");
        isValid = false;
      }
      if (!item.purchase_price) {
        errors[`purchase_price_${index}`] = t("pharmacy.purchasePriceRequired");
        isValid = false;
      }
      if (!item.sell_price) {
        errors[`sell_price_${index}`] = t("pharmacy.sellPriceRequired");
        isValid = false;
      }
      if (!item.expiry_date) {
        errors[`expiry_date_${index}`] = t("pharmacy.expiryDateRequired");
        isValid = false;
      }
    });

    setFormErrors(errors);

    if (isValid) {
      const payload = {
        date: input.date,
        medicine_company: input.medicine_company,
        items: input.items,
      };

      handleAddPurchase(payload);
    }
  };

  // Calculate the total price of all items
  const calculateTotalPrice = () => {
    return input.items
      .reduce((total, item) => total + item.subtotal, 0)
      .toFixed(2);
  };

  return (
    <Paper
      sx={{
        boxShadow: "0px 0px 16px rgba(0,0,0,0.1)",
        borderRadius: "12px",
        marginTop: "2rem",
        marginBottom: "2rem",
        padding: { xs: "1.5rem", md: "2.5rem" },
        width: "100%",
      }}
    >
      {/* Go Back Icon and Title */}
      <Box display="flex" alignItems="center" mb={4}>
        <Tooltip title={t("pharmacy.goBack")} arrow>
          <IconButton
            onClick={() => navigate(-1)}
            aria-label={t("pharmacy.goBack")}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon size="large" />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography fontWeight={600} variant="h4" color="primary" gutterBottom>
        {t("pharmacy.addPurchaseDrugs")}
      </Typography>

      <Divider sx={{ mt: 2, mb: 3, width: "100%" }} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("pharmacy.purchaseDate")}
            name="date"
            type="date"
            size="medium"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            value={input.date}
            onChange={handleChangePurchaseField}
            error={!!formErrors.date}
            helperText={formErrors.date}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            label={t("pharmacy.medicineCompany")}
            name="medicine_company"
            size="medium"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            value={input.medicine_company}
            onChange={handleChangePurchaseField}
          >
            {medicineCompanyData &&
              medicineCompanyData?.map((item, index) => (
                <MenuItem key={index} value={item.id}>
                  {"Company Name:" +
                    item.name +
                    " Phone:" +
                    item.phone +
                    " Address:" +
                    item.address}
                </MenuItem>
              ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("pharmacy.markupPercentage")}
            type="number"
            size="medium"
            variant="outlined"
            value={markupPercentage}
            onChange={handleChangeMarkup}
            helperText={t("pharmacy.enterMarkupPercentage")}
          />
        </Grid>
      </Grid>

      {/* Search Medicine Section */}
      <Box mb={4} mt={2}>
        <Typography variant="subtitle1" fontWeight={500} mb={2}>
          {t("pharmacy.addMedicine")}
        </Typography>
        <Autocomplete
          ref={autocompleteRef}
          value={null}
          inputValue={autocompleteInputValue}
          onInputChange={(event, newInputValue) => {
            setAutocompleteInputValue(newInputValue);
          }}
          onChange={(event, value) => {
            handleAddNewItem(value);
          }}
          options={medicines || []}
          getOptionLabel={(option) =>
            `${option.name} (${option.category_display})`
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("pharmacy.searchMedicine")}
              variant="outlined"
              placeholder={t("pharmacy.searchMedicinePlaceholder")}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{
                backgroundColor: "#fff",
                borderRadius: 2,
              }}
            />
          )}
        />
      </Box>

      <TableContainer component={Box} sx={{ mt: 4 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell align="center">
                <Typography fontWeight={600}>
                  {t("pharmacy.medicine")}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography fontWeight={600}>
                  {t("pharmacy.quantity")}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography fontWeight={600}>
                  {t("pharmacy.purchasePrice")}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography fontWeight={600}>
                  {t("pharmacy.sellPrice")}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography fontWeight={600}>
                  {t("pharmacy.expiryDate")}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography fontWeight={600}>
                  {t("pharmacy.subtotal")}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography fontWeight={600}>{t("pharmacy.action")}</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {input.items.map((item, index) => (
              <TableRow key={index}>
                <TableCell align="center">{item.name}</TableCell>
                <TableCell align="center">
                  <TextField
                    inputRef={(el) => (quantityRefs.current[index] = el)}
                    name="quantity"
                    type="number"
                    size="small"
                    variant="outlined"
                    value={item.quantity}
                    onChange={(e) => handleChangeItemField(index, e)}
                    error={!!formErrors[`quantity_${index}`]}
                    helperText={formErrors[`quantity_${index}`]}
                    onKeyDown={(e) => handleChangeItemField(index, e)}
                  />
                </TableCell>
                <TableCell align="center">
                  <TextField
                    inputRef={(el) => (purchasePriceRefs.current[index] = el)}
                    name="purchase_price"
                    type="number"
                    size="small"
                    variant="outlined"
                    value={item.purchase_price}
                    onChange={(e) => handleChangeItemField(index, e)}
                    error={!!formErrors[`purchase_price_${index}`]}
                    helperText={formErrors[`purchase_price_${index}`]}
                    onKeyDown={(e) => handleChangeItemField(index, e)}
                  />
                </TableCell>
                <TableCell align="center">
                  <TextField
                    inputRef={(el) => (sellPriceRefs.current[index] = el)}
                    name="sell_price"
                    type="number"
                    size="small"
                    variant="outlined"
                    value={item.sell_price}
                    onChange={(e) => handleChangeItemField(index, e)}
                    error={!!formErrors[`sell_price_${index}`]}
                    helperText={formErrors[`sell_price_${index}`]}
                    onKeyDown={(e) => handleChangeItemField(index, e)}
                  />
                </TableCell>
                <TableCell align="center">
                  <TextField
                    inputRef={(el) => (expiryDateRefs.current[index] = el)}
                    fullWidth
                    name="expiry_date"
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={item.expiry_date}
                    onChange={(e) => handleChangeItemField(index, e)}
                    error={!!formErrors[`expiry_date_${index}`]}
                    helperText={formErrors[`expiry_date_${index}`]}
                    onKeyDown={(e) => handleChangeItemField(index, e)}
                  />
                </TableCell>
                <TableCell align="center">
                  <Typography>{item.subtotal.toFixed(2)} ؋</Typography>
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveItem(index)}
                  >
                    <RemoveCircleOutline />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Display total price */}
      <Box mt={2} mb={4}>
        <Typography variant="h6" align="right" fontWeight={600}>
          {t("pharmacy.totalPrice")}: {calculateTotalPrice()} ؋
        </Typography>
      </Box>

      <Stack direction="row" justifyContent="flex-end" mt={4}>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={purchaseLoading}
          sx={{
            padding: "12px 30px",
            fontSize: "1rem",
            fontWeight: "bold",
            backgroundColor: purchaseLoading ? "#bdbdbd" : "#1976d2",
            color: "white",
            borderRadius: "8px",
            boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.2)",
            textTransform: "capitalize",
            "&:hover": {
              backgroundColor: purchaseLoading ? "#bdbdbd" : "#115293",
            },
          }}
        >
          {purchaseLoading ? t("pharmacy.saving") : t("pharmacy.savePurchase")}
        </Button>
      </Stack>
    </Paper>
  );
};

export default PurchaseDrug;
